import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { Pencil, Check, X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";

const inputCls = "h-6 rounded border border-slate-300 bg-white px-1 text-xs";
const safeCur = (c) => { const s = String(c || "").trim().toUpperCase(); return /^[A-Z]{3}$/.test(s) ? s : "CHF"; };
const fmtChf = (v, cur = "CHF") => {
  if (v == null || v === "") return "—";
  try { return new Intl.NumberFormat("de-CH", { style: "currency", currency: safeCur(cur) }).format(Number(v)); }
  catch { return `CHF ${Number(v).toFixed(2)}`; }
};

/** Datum direkt in der Posteingangs-Karte ändern (Klick auf den Stift → Datumsfeld → Enter/✓ speichert). */
export function InlineDateEdit({ companyId, item, field = "invoice_date", label, onUpdated }) {
  const value = item.extracted?.[field];
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setVal(value || ""), [value]);

  const save = async () => {
    if (!val || val === value) { setEditing(false); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/quick-edit`, { [field]: val });
      onUpdated?.({ ...item, extracted: r.data.extracted, checks: r.data.checks, routing: r.data.routing || item.routing, manually_edited_fields: r.data.manually_edited_fields });
      toast.success(`${label} auf ${formatDate(val)} geändert`);
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Datum konnte nicht gespeichert werden"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid={`inbox-quick-${field}-editor-${item.id}`}>
        {label}:
        <SwissDateField className={inputCls} value={val} autoFocus onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} data-testid={`inbox-quick-${field}-input-${item.id}`} />
        <button type="button" onClick={save} disabled={busy} className="text-emerald-700" title="Speichern" data-testid={`inbox-quick-${field}-save-${item.id}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
        <button type="button" onClick={() => setEditing(false)} className="text-slate-400" title="Abbrechen"><X className="h-3.5 w-3.5" /></button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 group">
      {label}: <b>{formatDate(value)}</b>
      <button type="button" onClick={() => setEditing(true)} className="text-slate-300 group-hover:text-slate-600 hover:!text-slate-900 transition-colors" title={`${label} direkt ändern`} data-testid={`inbox-quick-${field}-${item.id}`}>
        <Pencil className="h-3 w-3" />
      </button>
    </span>
  );
}

/** Betrag direkt in der Karte korrigieren – Hinweis + Bestätigung, wenn er vom OCR-Wert abweicht; MwSt wird im Backend neu gerechnet. */
export function InlineAmountEdit({ companyId, item, onUpdated }) {
  const ex = item.extracted || {};
  const value = ex.total_amount;
  const ocr = ex.ocr_total_amount ?? value;
  const cur = ex.currency || "CHF";
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value != null ? String(value) : "");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(value != null ? String(value) : ""); setConfirm(false); }, [value]);

  const num = parseFloat(String(val).replace(",", "."));
  const deviates = ocr != null && Number.isFinite(num) && Math.abs(num - Number(ocr)) >= 0.005;
  const close = () => { setEditing(false); setConfirm(false); };

  const save = async (force = false) => {
    if (!Number.isFinite(num) || num <= 0) { toast.error("Bitte einen gültigen Betrag > 0 eingeben"); return; }
    if (Math.abs(num - Number(value)) < 0.005) { close(); return; }
    if (deviates && !force) { setConfirm(true); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/quick-edit`, { total_amount: num });
      onUpdated?.({ ...item, extracted: r.data.extracted, checks: r.data.checks, routing: r.data.routing || item.routing, manually_edited_fields: r.data.manually_edited_fields });
      toast.success(`Betrag auf ${fmtChf(num, cur)} geändert${r.data.extracted?.tax_amount != null ? ` · MwSt ${fmtChf(r.data.extracted.tax_amount, cur)}` : ""}`);
      close();
    } catch (e) { toast.error(e.response?.data?.detail || "Betrag konnte nicht gespeichert werden"); }
    finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1 group">
        Betrag: <b>{fmtChf(value, cur)}</b>
        <button type="button" onClick={() => setEditing(true)} className="text-slate-300 group-hover:text-slate-600 hover:!text-slate-900 transition-colors" title="Betrag direkt korrigieren" data-testid={`inbox-quick-amount-${item.id}`}>
          <Pencil className="h-3 w-3" />
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col gap-1" data-testid={`inbox-quick-amount-editor-${item.id}`}>
      <span className="inline-flex items-center gap-1">
        Betrag:
        <input type="number" step="0.05" min="0.05" inputMode="decimal" className={`${inputCls} w-24 text-right ${deviates ? "border-amber-400 bg-amber-50" : ""}`} value={val} autoFocus disabled={busy}
          onChange={(e) => { setVal(e.target.value); setConfirm(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") save(confirm); if (e.key === "Escape") close(); }} data-testid={`inbox-quick-amount-input-${item.id}`} />
        <span className="text-slate-500">{cur}</span>
        {!confirm && (
          <button type="button" onClick={() => save(false)} disabled={busy} className="text-emerald-700" title="Speichern" data-testid={`inbox-quick-amount-save-${item.id}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
        )}
        <button type="button" onClick={close} className="text-slate-400" title="Abbrechen" data-testid={`inbox-quick-amount-cancel-${item.id}`}><X className="h-3.5 w-3.5" /></button>
      </span>
      {deviates && (
        <span className="inline-flex items-center gap-2 rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[11px] text-amber-800" data-testid={`inbox-quick-amount-hint-${item.id}`}>
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span>OCR hat <b>{fmtChf(ocr, cur)}</b> erkannt{confirm ? " – trotzdem übernehmen? MwSt wird neu gerechnet." : "."}</span>
          {confirm && (
            <button type="button" onClick={() => save(true)} disabled={busy} className="rounded bg-amber-600 px-1.5 py-0.5 font-medium text-white hover:bg-amber-700" data-testid={`inbox-quick-amount-confirm-${item.id}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Ja, übernehmen"}
            </button>
          )}
        </span>
      )}
    </span>
  );
}

/** Konto direkt per Dropdown wechseln (ohne Dialog); lernt wie der Stift-Dialog, aber ohne Regeln. */
export function InlineAccountSelect({ companyId, item, accounts, ensureAccounts, onUpdated, onOpenDialog }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = String(item.extracted?.suggested_account || "").slice(0, 4);

  const start = async () => { await ensureAccounts?.(); setOpen(true); };
  const change = async (code) => {
    const acc = accounts.find((a) => String(a.code) === code);
    if (!acc) return;
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${item.id}/account`, { account: `${acc.code} ${acc.name}` });
      onUpdated?.({ ...item, extracted: { ...item.extracted, suggested_account: `${acc.code} ${acc.name}`, account_source: "manual" }, account_manually_set: true });
      toast.success(`Konto geändert: ${acc.code} ${acc.name}`);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Konto konnte nicht gespeichert werden"); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <span className="inline-flex items-center gap-1">
        <button type="button" onClick={start} className="text-violet-500 hover:text-violet-900 transition-colors" title="Konto direkt wechseln" data-testid={`inbox-quick-account-${item.id}`}>
          <Pencil className="h-3 w-3" />
        </button>
        {onOpenDialog && (
          <button type="button" onClick={onOpenDialog} className="text-[10px] text-violet-500 hover:text-violet-900 underline-offset-2 hover:underline" title="Mit Regel («immer so buchen») anpassen" data-testid={`inbox-edit-account-${item.id}`}>
            Regel
          </button>
        )}
      </span>
    );
  }
  const expense = accounts.filter((a) => ["expense", "asset"].includes(a.type));
  const rest = accounts.filter((a) => !["expense", "asset"].includes(a.type));
  return (
    <span className="inline-flex items-center gap-1">
      <select className="h-6 max-w-[16rem] rounded border border-violet-300 bg-white px-1 text-xs" value={current} autoFocus disabled={busy} onChange={(e) => change(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)} data-testid={`inbox-quick-account-select-${item.id}`}>
        {!accounts.some((a) => String(a.code) === current) && <option value={current}>{current || "– wählen –"}</option>}
        <optgroup label="Aufwand / Aktiven">{expense.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}</optgroup>
        {rest.length > 0 && <optgroup label="Weitere">{rest.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}</optgroup>}
      </select>
      <button type="button" onClick={() => setOpen(false)} className="text-slate-400" title="Abbrechen"><X className="h-3.5 w-3.5" /></button>
    </span>
  );
}
